/**
 * Smart Table of Contents Generation System
 * Automatically generates professional TOC from chapter analysis and book structure
 */

import { ProjectState } from '../types/project';
import { Chapter, FrontMatter, BackMatter } from '../types';
import { calculateBookPageStructure, BookPageStructure, PageInfo, countWords } from './pageCalculation';

export interface TOCEntry {
  id: string;
  title: string;
  anchor: string; // Anchor link instead of page number
  level: number; // 0 = main chapter, 1 = section, 2 = subsection
  wordCount: number;
  type: 'frontMatter' | 'chapter' | 'backMatter';
  hasContent: boolean;
}

export interface ChapterSection {
  title: string;
  level: number;
  startPosition: number;
  content: string;
  wordCount: number;
}

export interface TOCStyle {
  id: string;
  name: string;
  description: string;
  showPageNumbers: boolean;
  showWordCounts: boolean;
  includeSubsections: boolean;
  dotFill: boolean;
  indentStyle: 'spaces' | 'dashes' | 'numbers';
  template: string;
}

export interface EnhancedTableOfContents {
  standardToc: string;
  detailedToc: string;
  digitalToc: Map<string, string>; // For hyperlinks (title -> anchor)
  visualToc: string; // With icons/symbols
  entries: TOCEntry[];
  metadata: {
    totalEntries: number;
    averageChapterLength: number;
    estimatedReadingTime: number;
  };
}

/**
 * Available TOC styles
 */
export const TOC_STYLES: TOCStyle[] = [
  {
    id: 'standard',
    name: 'Standard',
    description: 'Classic book style with links',
    showPageNumbers: false,
    showWordCounts: false,
    includeSubsections: false,
    dotFill: false,
    indentStyle: 'spaces',
    template: '[{title}](#{anchor})'
  },
  {
    id: 'detailed',
    name: 'Detailed',
    description: 'Includes subsections and word counts',
    showPageNumbers: false,
    showWordCounts: true,
    includeSubsections: true,
    dotFill: false,
    indentStyle: 'spaces',
    template: '{indent}[{title}](#{anchor}) ({words} words)'
  },
  {
    id: 'digital',
    name: 'Digital',
    description: 'Clean style for e-books without page numbers',
    showPageNumbers: false,
    showWordCounts: true,
    includeSubsections: true,
    dotFill: false,
    indentStyle: 'dashes',
    template: '{indent}[{title}](#{anchor}) ({words} words)'
  },
  {
    id: 'minimalist',
    name: 'Minimalist',
    description: 'Simple list with minimal formatting',
    showPageNumbers: false,
    showWordCounts: false,
    includeSubsections: false,
    dotFill: false,
    indentStyle: 'spaces',
    template: '[{title}](#{anchor})'
  },
  {
    id: 'academic',
    name: 'Academic',
    description: 'Numbered sections for academic works',
    showPageNumbers: false,
    showWordCounts: false,
    includeSubsections: true,
    dotFill: false,
    indentStyle: 'numbers',
    template: '{number} [{title}](#{anchor})'
  }
];

/**
 * Generate clean anchor ID from title
 */
export function generateAnchorId(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
}

/**
 * Extract sections and subsections from chapter content
 */
export function extractChapterSections(chapter: Chapter): ChapterSection[] {
  const content = chapter.previewContent || chapter.content || '';
  const sections: ChapterSection[] = [];

  if (!content) return sections;

  // Regex patterns for different heading levels
  const headingPatterns = [
    { regex: /^#{1}\s+(.+)$/gm, level: 1 }, // # Main sections
    { regex: /^#{2}\s+(.+)$/gm, level: 2 }, // ## Subsections
    { regex: /^#{3}\s+(.+)$/gm, level: 3 }, // ### Sub-subsections
  ];

  // Also look for common section patterns in prose
  const prosePatterns = [
    { regex: /^([A-Z][A-Z\s]+)$/gm, level: 1 }, // ALL CAPS sections
    { regex: /^\*\*(.+)\*\*$/gm, level: 2 }, // **Bold sections**
    { regex: /^_(.+)_$/gm, level: 2 }, // _Italic sections_
  ];

  const allMatches: { title: string; level: number; position: number }[] = [];

  // Extract markdown headings
  headingPatterns.forEach(pattern => {
    let match;
    while ((match = pattern.regex.exec(content)) !== null) {
      allMatches.push({
        title: match[1].trim(),
        level: pattern.level,
        position: match.index
      });
    }
  });

  // Extract prose sections (only if no markdown headings found)
  if (allMatches.length === 0) {
    prosePatterns.forEach(pattern => {
      let match;
      while ((match = pattern.regex.exec(content)) !== null) {
        allMatches.push({
          title: match[1].trim(),
          level: pattern.level,
          position: match.index
        });
      }
    });
  }

  // Sort by position and convert to sections
  allMatches.sort((a, b) => a.position - b.position);

  for (let i = 0; i < allMatches.length; i++) {
    const match = allMatches[i];
    const nextMatch = allMatches[i + 1];
    
    const sectionStart = match.position;
    const sectionEnd = nextMatch ? nextMatch.position : content.length;
    const sectionContent = content.slice(sectionStart, sectionEnd);

    sections.push({
      title: match.title,
      level: match.level,
      startPosition: sectionStart,
      content: sectionContent,
      wordCount: countWords(sectionContent)
    });
  }

  return sections;
}

/**
 * Generate TOC entries from project state
 */
export function generateTOCEntries(projectState: ProjectState): TOCEntry[] {
  const entries: TOCEntry[] = [];
  let entryId = 0;

  // Add front matter entries (from project state, not page structure)
  const frontMatter = projectState.frontMatter;
  if (frontMatter) {
    const frontMatterSections = [
      { key: 'dedication', title: 'Dedication' },
      { key: 'epigraph', title: 'Epigraph' },
      { key: 'foreword', title: 'Foreword' },
      { key: 'preface', title: 'Preface' },
      { key: 'acknowledgmentsFront', title: 'Acknowledgments' },
      { key: 'prologue', title: 'Prologue' },
      { key: 'introduction', title: 'Introduction' }
    ];

    frontMatterSections.forEach(section => {
      const content = (frontMatter as any)[section.key];
      if (content && content.trim()) {
        const wordCount = countWords(content);
        entries.push({
          id: `fm-${entryId++}`,
          title: section.title,
          anchor: generateAnchorId(section.title),
          level: 0,
          wordCount,
          type: 'frontMatter',
          hasContent: wordCount > 0
        });
      }
    });
  }

  // Add chapter entries with sections
  if (projectState.chapters && Array.isArray(projectState.chapters)) {
    projectState.chapters.forEach((chapter, chapterIndex) => {
      const chapterTitle = `Chapter ${chapterIndex + 1}: ${chapter.title || 'Untitled'}`;
      const content = chapter.previewContent || chapter.content || '';
      const wordCount = countWords(content);

      // Main chapter entry
      entries.push({
        id: `ch-${entryId++}`,
        title: chapterTitle,
        anchor: generateAnchorId(chapterTitle),
        level: 0,
        wordCount,
        type: 'chapter',
        hasContent: wordCount > 0
      });

      // Extract and add chapter sections
      const sections = extractChapterSections(chapter);
      sections.forEach(section => {
        entries.push({
          id: `sec-${entryId++}`,
          title: section.title,
          anchor: generateAnchorId(section.title),
          level: section.level,
          wordCount: section.wordCount,
          type: 'chapter',
          hasContent: true
        });
      });
    });
  }

  // Add back matter entries (from project state)
  const backMatter = projectState.backMatter;
  if (backMatter) {
    const backMatterSections = [
      { key: 'epilogue', title: 'Epilogue' },
      { key: 'afterword', title: 'Afterword' },
      { key: 'authorsNote', title: "Author's Note" },
      { key: 'acknowledgmentsBack', title: 'Acknowledgments' },
      { key: 'aboutTheAuthor', title: 'About the Author' },
      { key: 'readingGroupGuide', title: 'Reading Group Guide' },
      { key: 'teaserNextBook', title: 'Teaser for Next Book' }
    ];

    backMatterSections.forEach(section => {
      const content = (backMatter as any)[section.key];
      if (content && content.trim()) {
        const wordCount = countWords(content);
        entries.push({
          id: `bm-${entryId++}`,
          title: section.title,
          anchor: generateAnchorId(section.title),
          level: 0,
          wordCount,
          type: 'backMatter',
          hasContent: wordCount > 0
        });
      }
    });

    // Handle special back matter arrays
    if (backMatter.appendices && Array.isArray(backMatter.appendices) && backMatter.appendices.length > 0) {
      entries.push({
        id: `bm-${entryId++}`,
        title: 'Appendices',
        anchor: generateAnchorId('Appendices'),
        level: 0,
        wordCount: backMatter.appendices.reduce((sum, app) => sum + countWords(app.content || ''), 0),
        type: 'backMatter',
        hasContent: true
      });
    }

    if (backMatter.glossary && Array.isArray(backMatter.glossary) && backMatter.glossary.length > 0) {
      entries.push({
        id: `bm-${entryId++}`,
        title: 'Glossary',
        anchor: generateAnchorId('Glossary'),
        level: 0,
        wordCount: backMatter.glossary.length * 10, // Estimate words per glossary entry
        type: 'backMatter',
        hasContent: true
      });
    }

    if (backMatter.bibliography && Array.isArray(backMatter.bibliography) && backMatter.bibliography.length > 0) {
      entries.push({
        id: `bm-${entryId++}`,
        title: 'Bibliography',
        anchor: generateAnchorId('Bibliography'),
        level: 0,
        wordCount: backMatter.bibliography.length * 15, // Estimate words per bibliography entry
        type: 'backMatter',
        hasContent: true
      });
    }

    if (backMatter.index && backMatter.index.trim()) {
      entries.push({
        id: `bm-${entryId++}`,
        title: 'Index',
        anchor: generateAnchorId('Index'),
        level: 0,
        wordCount: countWords(backMatter.index),
        type: 'backMatter',
        hasContent: true
      });
    }
  }

  return entries;
}

/**
 * Format TOC according to specified style
 */
export function formatTOC(entries: TOCEntry[], style: TOCStyle): string {
  let toc = '';
  const numbering: { [level: number]: number } = {};

  entries.forEach(entry => {
    // Skip entries without content unless they're main chapters
    if (!entry.hasContent && entry.level > 0) return;

    // Skip subsections if style doesn't include them
    if (entry.level > 0 && !style.includeSubsections) return;

    let line = style.template;
    
    // Handle indentation
    let indent = '';
    if (entry.level > 0) {
      switch (style.indentStyle) {
        case 'spaces':
          indent = '  '.repeat(entry.level);
          break;
        case 'dashes':
          indent = '—'.repeat(entry.level) + ' ';
          break;
        case 'numbers':
          // Initialize numbering for this level
          if (!numbering[entry.level]) numbering[entry.level] = 0;
          numbering[entry.level]++;
          // Reset deeper levels
          Object.keys(numbering).forEach(level => {
            if (parseInt(level) > entry.level) {
              numbering[parseInt(level)] = 0;
            }
          });
          const numberStr = Object.keys(numbering)
            .filter(level => parseInt(level) <= entry.level && numbering[parseInt(level)] > 0)
            .map(level => numbering[parseInt(level)])
            .join('.');
          indent = numberStr + ' ';
          break;
      }
    }

    // Replace template variables
    line = line.replace('{indent}', indent);
    line = line.replace('{title}', entry.title);
    line = line.replace('{anchor}', entry.anchor);
    line = line.replace('{words}', style.showWordCounts ? entry.wordCount.toString() : '');
    line = line.replace('{number}', ''); // Handled in indent logic

    // Clean up extra spaces
    line = line.replace(/\s+/g, ' ').trim();

    toc += line + '\n';
  });

  return toc.trim();
}

/**
 * Generate hyperlinked TOC for digital formats
 */
export function generateDigitalTOC(entries: TOCEntry[]): Map<string, string> {
  const digitalToc = new Map<string, string>();
  
  entries.forEach(entry => {
    if (entry.hasContent || entry.level === 0) {
      digitalToc.set(entry.title, entry.anchor);
    }
  });

  return digitalToc;
}

/**
 * Generate visual TOC with icons and symbols
 */
export function generateVisualTOC(entries: TOCEntry[]): string {
  let visualToc = '';

  entries.forEach(entry => {
    if (!entry.hasContent && entry.level > 0) return;

    let icon = '';
    switch (entry.type) {
      case 'frontMatter':
        icon = '📄';
        break;
      case 'chapter':
        icon = entry.level === 0 ? '📖' : '📝';
        break;
      case 'backMatter':
        icon = '📋';
        break;
    }

    const indent = '  '.repeat(entry.level);
    const completionStatus = entry.hasContent ? '✓' : '○';
    
    visualToc += `${indent}${icon} ${entry.title} ${completionStatus}\n`;
  });

  return visualToc.trim();
}

/**
 * Calculate TOC metadata
 */
export function calculateTOCMetadata(entries: TOCEntry[]): EnhancedTableOfContents['metadata'] {
  const chapterEntries = entries.filter(e => e.type === 'chapter' && e.level === 0);
  const totalWords = entries.reduce((sum, entry) => sum + entry.wordCount, 0);
  
  return {
    totalEntries: entries.length,
    averageChapterLength: chapterEntries.length > 0 
      ? Math.round(chapterEntries.reduce((sum, e) => sum + e.wordCount, 0) / chapterEntries.length)
      : 0,
    estimatedReadingTime: Math.round(totalWords / 250) // Average reading speed: 250 words/minute
  };
}

/**
 * Generate comprehensive enhanced table of contents
 */
export function generateEnhancedTableOfContents(projectState: ProjectState): EnhancedTableOfContents {
  const entries = generateTOCEntries(projectState);
  const metadata = calculateTOCMetadata(entries);
  
  const standardStyle = TOC_STYLES.find(s => s.id === 'standard')!;
  const detailedStyle = TOC_STYLES.find(s => s.id === 'detailed')!;
  
  return {
    standardToc: formatTOC(entries, standardStyle),
    detailedToc: formatTOC(entries, detailedStyle),
    digitalToc: generateDigitalTOC(entries),
    visualToc: generateVisualTOC(entries),
    entries,
    metadata
  };
}

/**
 * Apply TOC to project state
 */
export function applyTOCToProject(
  projectState: ProjectState, 
  enhancedTOC: EnhancedTableOfContents, 
  selectedStyle: string = 'standard'
): Partial<ProjectState> {
  const style = TOC_STYLES.find(s => s.id === selectedStyle) || TOC_STYLES[0];
  const formattedTOC = formatTOC(enhancedTOC.entries, style);
  
  return {
    frontMatter: {
      ...projectState.frontMatter,
      tableOfContents: formattedTOC
    }
  };
}